import React, { useMemo } from 'react';
import { GoalStatusCard } from '../GoalStatusCard/GoalStatusCard';
import { useGoalsContext } from '../GoalsContext/GoalsContext';
import { GoalWeightValidation } from '../useGoalWeightValidation';
import { GoalBase, GoalId, GoalViewBy } from '../types';
import {
  getGroupId,
  getGroupName,
  groupGoals,
  ensureAllCategoryGroups,
} from './groupLogic';
import { GoalsListGroupedByEmployee } from './GoalsListGroupedByEmployee';

type Props = {
  goals: GoalBase[];
  onGoalClick: (goalId: GoalId) => void;
  viewBy: GoalViewBy;
  goalWeightValidation: GoalWeightValidation;
  onlyInvalidWeights?: boolean;
};

export const GoalsList: React.FC<React.PropsWithChildren<Props>> = ({
  goals,
  onGoalClick,
  viewBy,
  goalWeightValidation,
  onlyInvalidWeights = false,
}) => {
  const { employeeAssignedCategories, isManagerView } = useGoalsContext();
  const { invalidCategoryIds, errorsByCategoryId } = goalWeightValidation;

  const hasWeights = useMemo(
    () => employeeAssignedCategories.some((a) => a.weight != null),
    [employeeAssignedCategories],
  );

  const groupedGoals = useMemo(() => {
    const grouped = groupGoals(goals, viewBy);
    if (viewBy === 'category' && hasWeights) {
      return ensureAllCategoryGroups(grouped, employeeAssignedCategories);
    }
    return grouped;
  }, [goals, viewBy, hasWeights, employeeAssignedCategories]);

  const categoryWeightMap = useMemo(() => {
    const map = new Map<string, number | null>();
    for (const assignment of employeeAssignedCategories) {
      map.set(assignment.categoryId, assignment.weight ?? null);
    }
    return map;
  }, [employeeAssignedCategories]);

  if (viewBy === GoalViewBy.EMPLOYEE) {
    return (
      <GoalsListGroupedByEmployee
        goals={goals}
        goalWeightValidation={goalWeightValidation}
        onlyInvalidWeights={onlyInvalidWeights}
        onGoalClick={onGoalClick}
      />
    );
  }

  return (
    <div>
      {Array.from(groupedGoals.entries()).map(([groupKey, curGroupGoals]) => {
        const groupDescription =
          viewBy === 'category'
            ? curGroupGoals[0]?.category?.description ?? undefined
            : undefined;
        return (
          <GoalStatusCard
            key={groupKey}
            groupName={getGroupName(groupKey)}
            groupDescription={groupDescription}
            goals={curGroupGoals}
            onGoalClick={onGoalClick}
            categoryId={
              viewBy === 'category' ? getGroupId(groupKey) : undefined
            }
            categoryWeight={
              viewBy === 'category' && !isManagerView
                ? categoryWeightMap.get(getGroupId(groupKey)) ?? null
                : null
            }
            invalidCategoryIds={invalidCategoryIds}
            errorsByCategoryId={errorsByCategoryId}
          />
        );
      })}
    </div>
  );
};
